import {
  useRedirectPrompt,
  useRouteSummary
} from '$contexts/RouteContext/hooks'
import { BaseText } from '@genie-fintech/ui/components/fields'
import { BaseText as BaseTextHookField } from '@genie-fintech/ui/components/hook-fields'
import Textarea from '@genie-fintech/ui/components/hook-fields/Textarea'
import { Icon } from '@genie-fintech/ui/icons'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMount } from 'ahooks'
import { useCallback, useRef, useState } from 'react'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { z } from 'zod'
import { useAppBasicSettingService } from '$hooks/services'
import { useAppDetailStore, useAppScopeStore } from '$hooks/stores'
import { TAppBasicSetting, TAppBasicSettingPayload } from '$services/api'
import DragAndDropFileUpload from '$components/DragAndDropFileUpload'
import Select from '$components/Select'
import { useSaveEventListener } from '$hooks/actions/useSaveEventListener'
import SaveWithShortCutButton from '$components/SaveWithShortCutButton'
import { APP_ENVIRONMENT_OPTIONS, PERMISSIONS } from '$app/constants/common'
import { flexColumn } from '$styles/common.css'
import Asterisk from '$components/Asterisk'
import PublicKeyBlock from './PublicKeyBlock'
import { form, formMain } from './styles.css'
import Loading from '$components/Loading'
import { footnote, title } from '@genie-fintech/ui/style/typography'
import { themeVars } from '@genie-fintech/ui/style/theme'
import FooterAction from '$components/FooterAction'
import { useBreadcrumb } from '$components/Breadcrumb/hooks/useBreadcrumb'
import { APP_BRANDS } from '$app/constants'
import { usePermissions } from '$hooks/actions'

const { colors } = themeVars

const schema = z.object({
  name: z.string().trim().min(1, 'Requried!'),
  description: z.string().trim(),
  scope: z
    .object({
      value: z.string().trim(),
      label: z.string().trim()
    })
    .refine(d => !!d.value.trim(), 'Requried!'),
  environment: z
    .object({
      value: z.string().trim(),
      label: z.string().trim()
    })
    .refine(d => !!d.value.trim(), 'Requried!'),
  logo: z.object({
    key: z.string().trim(),
    url: z.string().trim()
  }),
  brand: z.string().trim().min(1, 'Required!')
})

type TFormValues = z.infer<typeof schema>

const AppBasicSetting = () => {
  const saveButtonRef = useRef<HTMLButtonElement>(null)

  const [showApiToken, setShowApiToken] = useState(false)

  const [showEncryptionSecret, setShowEncryptionSecret] = useState(false)

  const appName = useAppDetailStore(state => state.appDetail?.name) ?? ''

  const {
    updateAppBasicSettingAsync,
    updatingAppBasicSetting,
    fetchAppBasicSettingAsync,
    fetchingAppBasicSetting,
    appBasicSetting
  } = useAppBasicSettingService()

  const { route } = useRouteSummary()

  const { appId } = route.params

  const methods = useForm<TFormValues>({
    resolver: zodResolver(schema)
  })

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty },
    setValue
  } = methods

  useRedirectPrompt({ shouldPrompt: isDirty })

  useBreadcrumb([{ name: 'Basic Setting' }])

  const { checkPermissions } = usePermissions()

  const CAN_EDIT = checkPermissions([PERMISSIONS.APPLICATION_EDIT_BASIC])

  const appScopes = useAppScopeStore(state => state.appScopes)

  const transformToFormValues = useCallback(
    (data: TAppBasicSetting): TFormValues => {
      return {
        name: data.name ?? '',
        description: data.description ?? '',
        scope: appScopes.find(d => +d.value === data?.scope?.id) ?? {
          label: '',
          value: ''
        },
        environment: {
          label: data.environment.value.toLocaleUpperCase(),
          value: `${data.environment.key}`
        },
        logo: {
          key: data.logo?.key ?? '',
          url: data.logo?.url ?? ''
        },
        brand: data.brand ?? APP_BRANDS[0].name
      }
    },
    [appScopes]
  )

  useSaveEventListener(() => {
    saveButtonRef.current?.click()
  })

  useMount(() => {
    if (!appId) return

    fetchAppBasicSettingAsync({ application_id: appId }).then(({ data }) => {
      reset(transformToFormValues(data))
    })
  })

  const logo = useWatch({ name: 'logo', control })

  const brand = useWatch({ name: 'brand', control })

  const onSubmit = handleSubmit((formValues: TFormValues) => {
    if (!appId) return

    const {
      name,
      description,
      scope,
      logo: { key },
      brand,
      environment
    } = formValues

    const payload: TAppBasicSettingPayload = {
      name,
      description,
      scope_id: +scope.value,
      logo: { key },
      brand,
      environment: +environment.value
    }

    return updateAppBasicSettingAsync(appId, payload).then(() => {
      fetchAppBasicSettingAsync({ application_id: appId }).then(({ data }) => {
        reset(transformToFormValues(data))
      })
    })
  })

  const { api_token = '', encryption_key = '' } =
    appBasicSetting?.api_tokens.at(0) ?? {}

  if (fetchingAppBasicSetting) return <Loading />

  return (
    <>
      <form className={form} onSubmit={onSubmit}>
        <main className={formMain}>
          <article className={flexColumn} style={{ gap: 4 }}>
            <p className={title.two}>{appName}'s BASIC SETTING</p>
            <p className={footnote.one} style={{ color: colors.neutral[60] }}>
              Configure essential preferences and system options in the basic
              settings.
            </p>
          </article>

          <article className={flexColumn} style={{ gap: 20 }}>
            <BaseTextHookField
              control={control}
              name="name"
              label="Application Name"
              required
            />

            <Controller
              name="environment"
              control={control}
              render={({ field, fieldState: { error } }) => {
                return (
                  <article className="flex flex-col gap-y-1">
                    <label className="text-sm font-medium">
                      Environment
                      <Asterisk />
                    </label>
                    <Select
                      {...field}
                      options={APP_ENVIRONMENT_OPTIONS.map(v => ({
                        label: v.value.toLocaleUpperCase(),
                        value: `${v.key}`
                      }))}
                      error={!!error?.message}
                    />
                    {error?.message && (
                      <p className="text-xs text-[--colors-danger-default]">
                        {error.message}
                      </p>
                    )}
                  </article>
                )
              }}
            />

            <BaseText
              label="Client ID"
              inputProps={{
                readOnly: true,
                value: appBasicSetting?.client_id ?? ''
              }}
              required
              disabled
              affix={{ pre: <Icon namespace="Link" /> }}
            />

            <Textarea
              control={control}
              name="description"
              label="Description"
            />

            <Controller
              name="scope"
              control={control}
              render={({ field, fieldState: { error } }) => {
                return (
                  <article className="flex flex-col gap-y-1">
                    <label className="text-sm font-medium">
                      App Scope
                      <span className="text-[--colors-danger-default]">*</span>
                    </label>
                    <Select
                      {...field}
                      options={appScopes}
                      error={!!error?.message}
                    />
                    {error?.message && (
                      <p className="text-xs text-[--colors-danger-default]">
                        {error.message}
                      </p>
                    )}
                  </article>
                )
              }}
            />

            <BaseText
              label="API Token"
              inputProps={{
                readOnly: true,
                value: api_token,
                type: showApiToken ? 'text' : 'password',
                autoComplete: ''
              }}
              required
              disabled
              affix={{
                post: (
                  <Icon
                    className="cursor-pointer"
                    namespace={showApiToken ? 'EyeOff' : 'EyeOn'}
                    onClick={() => setShowApiToken(prev => !prev)}
                  />
                )
              }}
            />

            <BaseText
              label="Encryption Secret"
              inputProps={{
                readOnly: true,
                value: encryption_key,
                type: showEncryptionSecret ? 'text' : 'password',
                autoComplete: ''
              }}
              required
              disabled
              affix={{
                post: (
                  <Icon
                    className="cursor-pointer"
                    namespace={showEncryptionSecret ? 'EyeOff' : 'EyeOn'}
                    onClick={() => setShowEncryptionSecret(prev => !prev)}
                  />
                )
              }}
            />

            <Controller
              name="logo"
              control={control}
              render={() => {
                return (
                  <article className="flex flex-col gap-y-2">
                    <DragAndDropFileUpload
                      onUploadLogo={key =>
                        setValue('logo.key', key, { shouldDirty: true })
                      }
                      onChangeBrand={brand =>
                        setValue('brand', brand, { shouldDirty: true })
                      }
                      logo={logo}
                      brand={brand}
                      disabled={!CAN_EDIT}
                    />
                  </article>
                )
              }}
            />
          </article>
        </main>

        <FooterAction fullWidth={false}>
          <SaveWithShortCutButton
            disabled={!isDirty || !CAN_EDIT}
            loading={updatingAppBasicSetting}
          />
        </FooterAction>
      </form>

      <PublicKeyBlock />
    </>
  )
}

export default AppBasicSetting
